import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { TbDeviceFloppy, TbLock } from "react-icons/tb";
import { apiRequest } from "src/services/apiClient";
import { DetailCard } from "src/components/Details/Details";

interface Props {
    invoiceId: string;
    initialNotes: string;
    onSaved?: () => void;
}

export function InvoiceNotesTab({ invoiceId, initialNotes, onSaved }: Props) {
    const { t } = useTranslation();
    const [text, setText] = useState(initialNotes ?? "");
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        setText(initialNotes ?? "");
    }, [initialNotes]);

    const isDirty = text !== (initialNotes ?? "");

    const handleSave = async () => {
        setSaving(true);
        try {
            await apiRequest(`/invoices/${invoiceId}`, { method: "PATCH", body: JSON.stringify({ notes: text }) }, { "Content-Type": "application/json" });
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
            onSaved?.();
        } finally {
            setSaving(false);
        }
    };

    const handleClear = () => {
        setText("");
    };

    return (
        <DetailCard title={t("invoicePage.tab_notes")} full>
            <div className="inv-notes">
                <div className="inv-notes__banner">
                    <TbLock size={14} />
                    <span>{t("invoicePage.notesOnlyVisibleToYou")}</span>
                </div>
                <textarea
                    className="inv-notes__textarea"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={t("invoicePage.notesPlaceholder")}
                    rows={8}
                />
                <div className="inv-notes__actions">
                    {text.length > 0 && (
                        <button
                            type="button"
                            className="inv-notes__clear"
                            onClick={handleClear}
                            disabled={saving}
                        >
                            {t("invoicePage.notesClear")}
                        </button>
                    )}
                    <button
                        type="button"
                        className="inv-notes__save"
                        onClick={handleSave}
                        disabled={saving || !isDirty}
                    >
                        <TbDeviceFloppy size={15} />
                        {saving ? "…" : saved ? t("invoicePage.notesSaved") : t("invoicePage.notesSave")}
                    </button>
                </div>
            </div>
        </DetailCard>
    );
}
